import {
  EmploymentCategoryLabelKeys,
  EmploymentCategoryOrder,
  EmploymentInput,
  EmploymentInputSchema,
  InputText,
  getOtherIncomeTypeSubMenuList,
  isNonEmployedJobType,
  isRetiredJobType,
  translateFieldError,
} from '@applyonline/shared'
import {
  FORM_ID,
  PhoneExtInput,
  PhoneInput,
  ScrollableAlert,
  SelectInput,
  useTranslatedOptionEnum,
} from '@applyonline/shared/components'
import { helpTip, useFormAutoSave, useLengthInMonths } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram, EOtherIncomeType } from '@if/core/types'
import { range } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ValidationErrorMessage from '../../../../../packages/shared/validator/ValidationErrorMessage'
import { Constants } from '../../../types/constants'

interface Props {
  applicantData: EmploymentInput
  onApplicantChanged: (data: EmploymentInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
}

const EmploymentForm = ({ onApplicantChanged, applicantData, nextStepAfter, currentStep }: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation()
  const [displayMinIncomeMessage, setDisplayMinIncomeMessage] = useState(false)

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
    watch,
    control,
    subscribe,
  } = useForm<EmploymentInput>({
    mode: 'onBlur',
    resolver: yupResolver(EmploymentInputSchema),
    defaultValues: applicantData,
  })

  useFormAutoSave(subscribe, onApplicantChanged)
  const watchOtherIncomeTypes = watch('otherIncomes.0.typeId')
  const jobType = watch('currentJobs.0.jobType')
  const currentJobSalary = watch('currentJobs.0.annualSalary')
  const otherIncomeAnnualSalary = watch('otherIncomes.0.annualAmount')
  const watchYears = watch('currentJobs.0.years')
  const watchMonthsUnderOneYear = watch('currentJobs.0.monthsUnderOneYear')
  const { isLessThanOneYear } = useLengthInMonths<EmploymentInput>({
    financingProgram: EFinancingProgram.B2c,
    fieldPrefix: 'currentJobs.0',
    years: watchYears,
    monthsUnderOneYear: watchMonthsUnderOneYear,
    setValue,
    clearErrors,
  })
  const monthsError = errors.currentJobs?.[0]?.months
  const isRetired = isRetiredJobType(jobType)
  const isNonEmployed = isNonEmployedJobType(jobType)

  const onSubmit = (data: EmploymentInput) => {
    if (Number(currentJobSalary ?? 0) + Number(otherIncomeAnnualSalary ?? 0) >= Constants.MinIncomes) {
      onApplicantChanged(data)
      nextStepAfter(currentStep)
    } else {
      setDisplayMinIncomeMessage(true)
    }
  }

  useEffect(() => {
    helpTip()
  }, [applicantData])

  useEffect(() => {
    setValue('otherIncomes.0.jobType', jobType)
  }, [jobType, setValue])

  useEffect(() => {
    if (!watchOtherIncomeTypes) {
      setValue('otherIncomes.0.annualAmount', null)
      clearErrors('otherIncomes.0.annualAmount')
    }
  }, [clearErrors, setValue, watchOtherIncomeTypes])

  useEffect(() => {
    if (isNonEmployedJobType(jobType)) {
      setValue('currentJobs.0.annualSalary', null, { shouldValidate: true })
    }
  }, [jobType, setValue])

  useEffect(() => {
    if (isRetiredJobType(jobType)) {
      setValue('otherIncomes.0.typeId', EOtherIncomeType.RetirementIncome, { shouldValidate: true })
    } else if ((Number(getValues('otherIncomes.0.typeId')) as EOtherIncomeType) === EOtherIncomeType.RetirementIncome) {
      setValue('otherIncomes.0.typeId', null, { shouldValidate: true })
    }
  }, [getValues, jobType, setValue])

  const jobTypeOptions = useMemo(
    () =>
      EmploymentCategoryOrder.map((item) => (
        <option key={item} value={item}>
          {isNonEmployedJobType(item) ? t(EmploymentCategoryLabelKeys[item]) : t(`EJobType.${item}`, { ns: 'enum' })}
        </option>
      )),
    [t],
  )
  const otherIncomeTypeOptions = useTranslatedOptionEnum('EOtherIncomeType', getOtherIncomeTypeSubMenuList(), 'enum')

  return (
    <section className="step-content">
      <h3 className="form-question">{t('income.howMuch')}</h3>

      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <ScrollableAlert showAlert={displayMinIncomeMessage} type="warn" message={t('income.minAnnualSalary')} />
        <SelectInput
          id="employment_category"
          label="income.category"
          error={errors.currentJobs?.[0]?.jobType}
          disableEmptyValue
          defaultValue=""
          hasEmptyValue
          options={jobTypeOptions}
          {...register('currentJobs.0.jobType')}
        />

        {!isNonEmployed && (
          <>
            <div className={`control-group  ${errors.currentJobs?.[0]?.annualSalary && 'error'}`}>
              <label htmlFor="gross_anual">
                {t('income.beforeDeductions')}
                <span className="help-tip">
                  <p>{t('income.grossAnnualInfo')}</p>
                </span>
              </label>
              <div className={`number-wrap ${resolvedLanguage === 'en' ? 'startwith' : 'endwith'}`}>
                <InputText
                  type="number"
                  inputMode="decimal"
                  title={t('income.grossAnnualInfo')}
                  id="annualSalary"
                  error={errors.currentJobs?.[0]?.annualSalary}
                  {...register('currentJobs.0.annualSalary')}
                />
              </div>
            </div>
            <div className={`control-group ${errors.currentJobs?.[0]?.jobTitle && 'error'}`}>
              <label htmlFor="job_title">{t('income.jobTitle')}</label>
              <input type="text" id="jobTitle" {...register('currentJobs.0.jobTitle')} maxLength={50} />
              <ValidationErrorMessage message={errors?.currentJobs?.[0]?.jobTitle?.message} />
            </div>

            <div className="control-group-wrap" style={{ alignItems: 'start' }}>
              <div className={`control-group mid ${errors.currentJobs?.[0]?.employerName && 'error'}`}>
                <label htmlFor="employer_name">{t('income.employerName')}</label>
                <input type="text" id="employerName" {...register('currentJobs.0.employerName')} maxLength={100} />
                <ValidationErrorMessage message={errors?.currentJobs?.[0]?.employerName?.message} />
              </div>

              <PhoneInput
                className="sml"
                control={control}
                name="currentJobs.0.employerPhone"
                label={t('income.phoneNumber')}
              />
              <PhoneExtInput
                className="sml"
                control={control}
                name="currentJobs.0.employerPhoneExt"
                label={t('income.extension')}
              />
            </div>

            <div className="control-group-wrap" style={{ alignItems: 'start' }}>
              <div className="control-group mid">
                <label htmlFor="employment_years">{t('income.employmentLenght')}</label>
                <div className={`dropdown-wrap ${!isLessThanOneYear && monthsError && 'error'}`}>
                  <select id="year" {...register('currentJobs.0.years')} defaultValue={0}>
                    <option value={0}>{t('common.years')}</option>
                    <option value={-1}>{t('common.lessThan1year')}</option>
                    {range(1, 15).map((item) => (
                      <option value={item} key={item}>
                        {t('common.yearsLabel', { count: item })}
                      </option>
                    ))}
                    <option value={15}>
                      {t('common.moreThan')} {t('common.yearsLabel', { count: 15 })}
                    </option>
                  </select>
                  {!isLessThanOneYear && <ValidationErrorMessage message={monthsError?.message} />}
                </div>
              </div>

              {isLessThanOneYear && (
                <div className="control-group mid">
                  <label htmlFor="month">{t('common.months')}</label>
                  <div className={`dropdown-wrap ${monthsError && 'error'}`}>
                    <select id="month" {...register('currentJobs.0.monthsUnderOneYear')} defaultValue={0}>
                      <option value={0}>{t('common.months')}</option>
                      {range(1, 12).map((item) => (
                        <option value={item} key={item}>
                          {t('common.monthsLabel', { count: item })}
                        </option>
                      ))}
                    </select>
                    <ValidationErrorMessage message={monthsError?.message} />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <div className="control-group">
          <label htmlFor="gross_anual">
            <h1 className="h3">
              {isRetired ? t('income.retirementIncome') : t('income.otherIncome')}
              {!isRetired && (
                <span className="help-tip">
                  <p>{t('income.otherIncomeInfo')}</p>
                </span>
              )}
            </h1>
          </label>
        </div>
        {!isRetired && (
          <div className="control-group mid">
            <label htmlFor="otherIncomeType">{t('income.otherIncome')}</label>
            <div className={`dropdown-wrap ${errors.otherIncomes?.[0]?.typeId && 'error'}`}>
              <select id="otherIncomeType" {...register('otherIncomes.0.typeId')}>
                <option aria-label={t('common.none')}> </option>
                {otherIncomeTypeOptions}
              </select>
            </div>
            {errors.otherIncomes?.[0]?.typeId && (
              <p className="error-message" style={{ position: 'absolute' }}>
                {translateFieldError(t, errors.otherIncomes?.[0]?.typeId)}
              </p>
            )}
          </div>
        )}
        <div className={`control-group mid ${errors.otherIncomes?.[0]?.annualAmount && 'error'}`}>
          <label htmlFor="other_source">{t('income.annualIncome')}</label>
          <div className={`number-wrap ${resolvedLanguage === 'en' ? 'startwith' : 'endwith'}`}>
            <input
              type="number"
              inputMode="decimal"
              id="annualAmount"
              disabled={!watchOtherIncomeTypes}
              {...register('otherIncomes.0.annualAmount')}
            />
            {errors.otherIncomes?.[0]?.annualAmount && (
              <p className="error-message" style={{ position: 'absolute' }}>
                {translateFieldError(t, errors.otherIncomes?.[0]?.annualAmount, { context: 'annualAmount' })}
              </p>
            )}
          </div>
        </div>
        {(Number(watchOtherIncomeTypes) as EOtherIncomeType) === EOtherIncomeType.Other && (
          <div className={`control-group ${errors.otherIncomes?.[0]?.description && 'error'}`}>
            <label htmlFor="other_source">{t('income.otherIncomeDescription')}</label>
            <input type="text" id="description" {...register('otherIncomes.0.description')} maxLength={50} />
            <ValidationErrorMessage message={errors?.otherIncomes?.[0]?.description?.message} />
          </div>
        )}
      </form>
    </section>
  )
}

export default EmploymentForm
